import React, { useCallback, useEffect, useState } from 'react';
import { ChildProfile, DeletedChildProfile, ChildDeletionEvent } from '../../models/childProfile';
import { ChildProfileDataService } from '../../services/childProfileDataService';
import { formatDisplayDate, toDateString } from '../../services/dateUtils';
import ConfirmationModal from '../ConfirmationModal';
import ChildAvatar from '../ChildAvatar';
import HardDeleteModal from './HardDeleteModal';

interface LeaversPanelProps {
    /** Called after a restore or permanent deletion — anything that changes the
     *  leaver population — so the parent can refresh the children grid and its
     *  leaver count (which decides whether the Leavers tab is shown at all). */
    onChanged?: () => void;
}

const EVENT_LABELS: Record<ChildDeletionEvent['event_type'], string> = {
    soft_delete: 'Marked as left',
    restore: 'Restored',
    due_date_change: 'Deletion date changed',
    notified: 'Retention reminder emailed',
    hard_delete_requested: 'Permanent deletion requested',
    hard_delete_completed: 'Permanently deleted',
    hard_delete_failed: 'Permanent deletion failed',
};

// Urgency tone for the card's status pill. The exact due date always sits in
// the facts list below it, so the pill only has to carry how soon that is.
function dueStatus(row: DeletedChildProfile): { tone: 'scheduled' | 'soon' | 'overdue' | 'purging'; label: string } {
    if (row.purge_requested_at) return { tone: 'purging', label: 'Deleting…' };
    const days = row.days_until_hard_delete;
    if (days === null) return { tone: 'scheduled', label: 'Scheduled' };
    if (days <= 0) return { tone: 'overdue', label: 'Due for deletion' };
    if (days <= 30) return { tone: 'soon', label: `Due in ${days} day${days === 1 ? '' : 's'}` };
    return { tone: 'scheduled', label: 'Scheduled' };
}

/**
 * Leavers & Deletion tab (child-deletion epic, Phase 1): lists soft-deleted
 * children with their retention status; supports restore, due-date override
 * and the per-child proof-ledger history. The hard-delete action itself
 * arrives with the Phase 2 purge engine and is shown disabled until then.
 */
const LeaversPanel: React.FC<LeaversPanelProps> = ({ onChanged }) => {
    const [rows, setRows] = useState<DeletedChildProfile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Row-action state
    const [restoreTarget, setRestoreTarget] = useState<DeletedChildProfile | null>(null);
    const [restoring, setRestoring] = useState(false);
    const [dueDateTarget, setDueDateTarget] = useState<DeletedChildProfile | null>(null);
    const [newDueAt, setNewDueAt] = useState('');
    const [dueDateReason, setDueDateReason] = useState('');
    const [savingDueDate, setSavingDueDate] = useState(false);
    const [dueDateError, setDueDateError] = useState<string | null>(null);
    const [historyTarget, setHistoryTarget] = useState<DeletedChildProfile | null>(null);
    const [historyEvents, setHistoryEvents] = useState<ChildDeletionEvent[] | null>(null);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [hardDeleteTarget, setHardDeleteTarget] = useState<DeletedChildProfile | null>(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setRows(await ChildProfileDataService.getDeleted());
        } catch (err) {
            console.error('Failed to load leavers:', err);
            setError('Failed to load the leavers list.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRows(); }, [fetchRows]);

    const handleRestore = async () => {
        if (!restoreTarget) return;
        setRestoring(true);
        try {
            await ChildProfileDataService.restore(restoreTarget.id);
            setRestoreTarget(null);
            await fetchRows();
            onChanged?.();
        } catch (err: any) {
            console.error('Restore failed:', err);
            setError(err?.response?.data?.error || 'Failed to restore the child.');
            setRestoreTarget(null);
        } finally {
            setRestoring(false);
        }
    };

    const openDueDateModal = (row: DeletedChildProfile) => {
        setDueDateTarget(row);
        setNewDueAt(row.hard_delete_due_at || toDateString(new Date()));
        setDueDateReason('');
        setDueDateError(null);
    };

    const handleSaveDueDate = async () => {
        if (!dueDateTarget) return;
        if (!newDueAt) { setDueDateError('Please choose a date.'); return; }
        if (!dueDateReason.trim()) { setDueDateError('A reason is required — it is recorded in the deletion log.'); return; }
        setSavingDueDate(true);
        setDueDateError(null);
        try {
            await ChildProfileDataService.updateHardDeleteDueDate(dueDateTarget.id, newDueAt, dueDateReason.trim());
            setDueDateTarget(null);
            await fetchRows();
        } catch (err: any) {
            console.error('Due-date update failed:', err);
            setDueDateError(err?.response?.data?.error || 'Failed to update the deletion date.');
        } finally {
            setSavingDueDate(false);
        }
    };

    const openHistory = async (row: DeletedChildProfile) => {
        setHistoryTarget(row);
        setHistoryEvents(null);
        setHistoryError(null);
        try {
            setHistoryEvents(await ChildProfileDataService.getDeletionEvents(row.id));
        } catch (err) {
            console.error('Failed to load deletion events:', err);
            setHistoryError('Failed to load the deletion history.');
        }
    };

    return (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div className="pill-toolbar" role="toolbar" aria-label="Leavers toolbar">
                <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    Children marked as left. Their data stays hidden and untouched until the deletion
                    date, when an admin can permanently delete it. Restore is available until then.
                </div>
                <div className="pill-toolbar__spacer" />
                <button
                    className="pill-toolbar__icon-btn"
                    onClick={fetchRows}
                    disabled={loading}
                    title="Reload leavers"
                    aria-label="Reload leavers"
                >
                    <i
                        className="fas fa-sync-alt"
                        style={loading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined}
                    />
                </button>
            </div>

            <div className="children-grid-pane" style={{ flex: 1, minWidth: 0, overflowY: 'auto', padding: '1.25rem 1.5rem 2rem' }}>
                {error && <div className="alert alert-danger py-2">{error}</div>}

                {loading && rows.length === 0 ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                        <div className="spinner-border text-success" role="status" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="text-muted py-5 text-center">
                        No leavers. Mark a child as left from their profile in the Child Profiles tab.
                    </div>
                ) : (
                    <div
                        className="children-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                            gap: '1rem',
                        }}
                    >
                        {rows.map(row => {
                            const status = dueStatus(row);
                            const purging = !!row.purge_requested_at;
                            const avatarChild = {
                                id: row.id,
                                initials: row.initials,
                                first_name: row.first_name,
                                last_name: row.last_name,
                                is_active: false,
                                colour_code: row.colour_code,
                            } as ChildProfile;
                            return (
                                <div key={row.id} className={`child-card leaver-card${purging ? ' is-purging' : ''}`}>
                                    <ChildAvatar child={avatarChild} size={72} imageUrl={null} showBadge={false} />
                                    <div className="child-card__body">
                                        <div className="child-card__name">{row.first_name} {row.last_name}</div>
                                        {row.alias && <div className="child-card__alias">"{row.alias}"</div>}
                                    </div>
                                    <span className={`leaver-card__status leaver-card__status--${status.tone}`}>
                                        {status.label}
                                    </span>
                                    <dl className="leaver-card__facts">
                                        <div>
                                            <dt>Left</dt>
                                            <dd>{formatDisplayDate(row.left_at)}</dd>
                                        </div>
                                        <div>
                                            <dt>Marked by</dt>
                                            <dd title={`${row.soft_deleted_by_name ?? ''} on ${formatDisplayDate(row.soft_deleted_at)}`}>
                                                {row.soft_deleted_by_name ?? '—'}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>Deletion due</dt>
                                            <dd>{row.hard_delete_due_at ? formatDisplayDate(row.hard_delete_due_at) : '—'}</dd>
                                        </div>
                                        <div>
                                            <dt>Last reminder</dt>
                                            <dd>{row.last_notified_at ? formatDisplayDate(row.last_notified_at) : '—'}</dd>
                                        </div>
                                        {row.hard_delete_reason && (
                                            <div>
                                                <dt>Note</dt>
                                                <dd title={row.hard_delete_reason}>{row.hard_delete_reason}</dd>
                                            </div>
                                        )}
                                    </dl>
                                    <div className="leaver-card__actions" data-help="admin.leaver-restore">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-success"
                                            onClick={() => setRestoreTarget(row)}
                                            disabled={purging}
                                            title={purging ? 'A permanent deletion is in progress — restore is no longer possible' : 'Undo the soft delete'}
                                        >
                                            <i className="fas fa-undo me-1" />Restore
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => openDueDateModal(row)}
                                            disabled={purging}
                                            title="Change the permanent-deletion date"
                                        >
                                            <i className="fas fa-calendar-alt me-1" />Change date
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => openHistory(row)}
                                            title="View the deletion log for this child"
                                        >
                                            <i className="fas fa-history me-1" />View log
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => setHardDeleteTarget(row)}
                                            disabled={purging}
                                            title={purging
                                                ? 'A permanent deletion is already in progress'
                                                : 'Permanently delete all records for this child'}
                                        >
                                            <i className="fas fa-trash me-1" />Delete
                                        </button>
                                    </div>
                                    <div className="child-card__bar" style={{ background: row.colour_code }} aria-hidden="true" />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Hard-delete flow */}
            {hardDeleteTarget && (
                <HardDeleteModal
                    row={hardDeleteTarget}
                    onClose={(didDelete) => {
                        setHardDeleteTarget(null);
                        if (didDelete) {
                            fetchRows();
                            onChanged?.();
                        }
                    }}
                />
            )}

            {/* Restore confirmation */}
            <ConfirmationModal
                show={!!restoreTarget}
                title="Restore child?"
                message={restoreTarget
                    ? `${restoreTarget.first_name} ${restoreTarget.last_name} will be restored as an inactive child. ` +
                      `The leaving date and scheduled deletion are cleared; reactivate the profile from the Child Profiles tab when ready.`
                    : ''}
                confirmText={restoring ? 'Restoring…' : 'Restore'}
                isDeleting={false}
                onConfirm={handleRestore}
                onCancel={() => setRestoreTarget(null)}
            />

            {/* Due-date override modal */}
            {dueDateTarget && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content ivory-panel">
                            <div className="modal-header">
                                <h5 className="modal-title">Change permanent-deletion date</h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setDueDateTarget(null)} />
                            </div>
                            <div className="modal-body">
                                {dueDateError && <div className="alert alert-danger py-2">{dueDateError}</div>}
                                <p className="mb-3">
                                    <strong>{dueDateTarget.first_name} {dueDateTarget.last_name}</strong> is currently
                                    scheduled for permanent deletion on{' '}
                                    <strong>{formatDisplayDate(dueDateTarget.hard_delete_due_at)}</strong>.
                                </p>
                                <div className="mb-3">
                                    <label className="form-label">New date</label>
                                    <input type="date" className="form-control" value={newDueAt} onChange={e => setNewDueAt(e.target.value)} />
                                </div>
                                <div className="mb-1">
                                    <label className="form-label">Reason <span className="text-danger">*</span></label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        maxLength={500}
                                        value={dueDateReason}
                                        onChange={e => setDueDateReason(e.target.value)}
                                        placeholder="e.g. parental erasure request ref 123 / safeguarding retention"
                                    />
                                    <small className="text-muted">Recorded in the deletion log.</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setDueDateTarget(null)} disabled={savingDueDate}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleSaveDueDate} disabled={savingDueDate}>
                                    {savingDueDate ? 'Saving…' : 'Save date'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Deletion-log modal */}
            {historyTarget && (
                <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content ivory-panel">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    Deletion log — {historyTarget.first_name} {historyTarget.last_name}
                                </h5>
                                <button type="button" className="btn-close" aria-label="Close" onClick={() => setHistoryTarget(null)} />
                            </div>
                            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                {historyError && <div className="alert alert-danger py-2">{historyError}</div>}
                                {!historyEvents && !historyError && (
                                    <div className="d-flex justify-content-center py-4">
                                        <div className="spinner-border text-success" role="status" />
                                    </div>
                                )}
                                {historyEvents && historyEvents.length === 0 && (
                                    <div className="text-muted">No events recorded.</div>
                                )}
                                {historyEvents && historyEvents.length > 0 && (
                                    <table className="table table-sm align-middle mb-0">
                                        <thead>
                                            <tr><th>When</th><th>Event</th><th>By</th><th>Details</th></tr>
                                        </thead>
                                        <tbody>
                                            {historyEvents.map(ev => (
                                                <tr key={ev.id}>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDisplayDate(ev.occurred_at)}</td>
                                                    <td>{EVENT_LABELS[ev.event_type] ?? ev.event_type}</td>
                                                    <td>{ev.actor_name ?? <span className="text-muted">System</span>}</td>
                                                    <td>
                                                        {ev.detail ? (
                                                            <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                                                {ev.event_type === 'due_date_change' && ev.detail.oldDueAt
                                                                    ? `${formatDisplayDate(ev.detail.oldDueAt)} → ${formatDisplayDate(ev.detail.newDueAt)}${ev.detail.reason ? ` — ${ev.detail.reason}` : ''}`
                                                                    : ev.event_type === 'soft_delete'
                                                                        ? `Left ${formatDisplayDate(ev.detail.leftAt)}; deletion due ${formatDisplayDate(ev.detail.hardDeleteDueAt)}${ev.detail.reason ? ` — ${ev.detail.reason}` : ''}`
                                                                        : JSON.stringify(ev.detail)}
                                                            </span>
                                                        ) : <span className="text-muted">—</span>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-primary" onClick={() => setHistoryTarget(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeaversPanel;
